import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TerminalsTab } from './TerminalsTab';
import { PaymentIssuesCard } from './PaymentIssuesCard';
import './OverviewTab.css';

/**
 * Merchant Admin Overview Tab Component
 * Displays merchant-specific metrics with terminals breakdown for Merchant Admins
 */
export function MerchantAdminOverviewTab({ summary, terminals, paymentIssues, loading, accountId, accountName, onNavigateToTab }) {
  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function handleMetricClick(tab) {
    if (onNavigateToTab) {
      onNavigateToTab(tab);
    }
  }

  // Compact Metric Card Component
  function CompactMetricCard({ icon, label, value, subtitle, color = 'primary', clickable, onClick }) {
    return (
      <div
        className={`metric-card-compact ${color} ${clickable ? 'clickable' : ''}`}
        onClick={clickable ? onClick : undefined}
      >
        <div className="metric-icon">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Calculate values for display
  const totalTransactions = parseInt(summary?.total_transactions || 0, 10);
  const paidTransactions = parseInt(summary?.paid_transactions || 0, 10);
  const pendingTransactions = parseInt(summary?.pending_transactions || 0, 10);
  const totalAmountPaid = parseFloat(summary?.total_amount_paid || 0);
  const totalFeesCollected = parseFloat(summary?.total_fees_collected || 0);
  // Total Due = Total Sales - Total Fees (what merchant receives)
  // Use API value if available, otherwise calculate client-side
  const totalDue = summary?.total_due_usd != null
    ? parseFloat(summary.total_due_usd)
    : totalAmountPaid - totalFeesCollected;
  const activeTerminals = (terminals || []).filter(t => t.is_active).length;
  const avgTransactionValue = paidTransactions > 0 ? totalAmountPaid / paidTransactions : 0;

  // Discrepancy metrics (gain/loss from auto-accepted and resolved discrepancies)
  const discrepancySummary = summary?.discrepancy_summary || {};
  const netDiscrepancyUsd = parseFloat(discrepancySummary?.net_discrepancy_usd || 0);
  const autoAccepted = discrepancySummary?.auto_accepted || {};
  const needsResolution = discrepancySummary?.needs_resolution || {};
  const autoAcceptedCount = (parseInt(autoAccepted?.underfunded_count || 0, 10) +
                            parseInt(autoAccepted?.overfunded_count || 0, 10));
  const needsResolutionCount = (parseInt(needsResolution?.underfunded_count || 0, 10) +
                                parseInt(needsResolution?.overfunded_count || 0, 10));
  const hasDiscrepancyData = autoAcceptedCount > 0 || needsResolutionCount > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {accountName ? `${accountName} Overview` : 'Merchant Overview'}
      </Typography>

      {/* Activity Summary Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Activity Summary</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'receipt']}
            label="Total Transactions"
            value={formatNumber(totalTransactions)}
            subtitle="In selected period"
            color="primary"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'check-circle']}
            label="Paid Transactions"
            value={formatNumber(paidTransactions)}
            subtitle={`${pendingTransactions} pending`}
            color="success"
            clickable
            onClick={() => handleMetricClick('transactions')}
          />
          <CompactMetricCard
            icon={['fal', 'dollar-sign']}
            label="Total Sales"
            value={formatCurrency(totalAmountPaid)}
            subtitle="Gross revenue"
            color="primary"
          />
          <CompactMetricCard
            icon={['fal', 'wallet']}
            label="Total Due"
            value={formatCurrency(totalDue)}
            subtitle="After fees"
            color="success"
          />
          {hasDiscrepancyData && (
            <CompactMetricCard
              icon={['fal', 'balance-scale']}
              label="Discrepancy Impact"
              value={formatCurrency(netDiscrepancyUsd)}
              subtitle={
                needsResolutionCount > 0
                  ? `${needsResolutionCount} need${needsResolutionCount === 1 ? 's' : ''} action`
                  : `${autoAcceptedCount} auto-accepted`
              }
              color={netDiscrepancyUsd >= 0 ? 'success' : 'warning'}
            />
          )}
        </div>
      </div>

      {/* Payment Issues Alert Card */}
      <PaymentIssuesCard
        paymentIssues={paymentIssues}
        onNavigateToTab={onNavigateToTab}
      />

      {/* Fees & Terminals Section */}
      <div className="metrics-section">
        <div className="metrics-section-title">Fees & Terminals</div>
        <div className="metrics-grid">
          <CompactMetricCard
            icon={['fal', 'hand-holding-usd']}
            label="Fees Paid"
            value={formatCurrency(totalFeesCollected)}
            subtitle="Total fees collected"
            color="warning"
          />
          <CompactMetricCard
            icon={['fal', 'tablet-alt']}
            label="Active Terminals"
            value={formatNumber(activeTerminals)}
            subtitle={`${(terminals || []).length} total`}
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'percentage']}
            label="Effective Rate"
            value={totalAmountPaid > 0 ? `${((totalFeesCollected / totalAmountPaid) * 100).toFixed(2)}%` : '0.00%'}
            subtitle="Fees / Revenue"
            color="info"
          />
          <CompactMetricCard
            icon={['fal', 'clock']}
            label="Pending"
            value={formatNumber(pendingTransactions)}
            subtitle="Awaiting payment"
            color={pendingTransactions > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </div>

      {/* Terminals Section */}
      <Box mt={4}>
        <TerminalsTab terminals={terminals || []} accountId={accountId} />
      </Box>
    </Box>
  );
}
